import { useMutation, useQueryClient } from 'react-query';
import { restoreTemplateVersion } from '../utils/queries';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTemplateEditorDispatch, useTemplateEditorState } from '../context/UseTemplateEditorContext';
import { useParams } from 'react-router';
import useNotifications from 'core/notifications/useNotifications';

export const useRestoreTemplateVersion = () => {
  const { t } = useTranslation('designs');
  const queryClient = useQueryClient();
  const { showNotification } = useNotifications();

  const { mutate: restoreTemplate } = useMutation(restoreTemplateVersion);

  const dispatch = useTemplateEditorDispatch();

  const { editorSettings, previewingVersion: versionId } = useTemplateEditorState();

  const { siteId, id: templateId, language } = useParams<{
    siteId: string;
    id: string;
    language: string;
  }>();

  return useCallback(() => {
    restoreTemplate(
      {
        siteId: siteId,
        templateId: templateId,
        language: language,
        versionId: versionId,
        t: t,
      },
      {
        onSuccess: () => {
          showNotification(t('designGallery.templateEditor.queries.restore.success'), 'normal');
          dispatch({
            type: 'SET_TEMPLATE_VERSION',
            payload: { versionId: '' },
          });
          dispatch({
            type: 'SET_EDITOR_SETTINGS',
            payload: {
              ...editorSettings,
              readOnly: false,
            },
          });
          queryClient.invalidateQueries({ queryKey: ['templateHistory'] });
        },
        onError: (error: Error) => {
          showNotification(error.message, 'error');
        },
      },
    );
  }, [
    dispatch,
    editorSettings,
    language,
    queryClient,
    restoreTemplate,
    showNotification,
    siteId,
    t,
    templateId,
    versionId,
  ]);
};
